/**
 * RankingTorneo.js — Ranking general y por fecha de un torneo.
 *
 * Props:
 *   torneoId  {string}  - ID del torneo
 *   session   {object}  - Sesión de Supabase
 *   tipoColor {string}  - Color del tipo de torneo (#00f2ff táctico, #a855f7 libre)
 */
import React, { useState, useEffect } from 'react';
import { supabase } from '../../../supabaseClient';

const MEDALLAS = ['🥇', '🥈', '🥉'];

const RankingTorneo = ({ torneoId, session, tipoColor = '#00f2ff' }) => {
  const [vista, setVista]         = useState('general'); // 'general' | fecha_id
  const [ranking, setRanking]     = useState([]);
  const [fechas, setFechas]       = useState([]);
  const [rankingFecha, setRankingFecha] = useState([]);
  const [loading, setLoading]     = useState(true);

  useEffect(() => {
    if (!torneoId) return;
    cargarDatos();
  }, [torneoId]);

  useEffect(() => {
    if (vista !== 'general' && vista) cargarFecha(vista);
  }, [vista]);

  const cargarDatos = async () => {
    setLoading(true);
    const [{ data: general }, { data: fs }] = await Promise.all([
      supabase
        .from('ranking_torneo')
        .select('*, perfiles(clan_tag)')
        .eq('torneo_id', torneoId)
        .order('posicion', { ascending: true }),
      supabase
        .from('torneo_fechas')
        .select('id, numero_fecha, nombre, activa, cerrada')
        .eq('torneo_id', torneoId)
        .order('numero_fecha', { ascending: true }),
    ]);
    setRanking(general || []);
    setFechas(fs || []);
    setLoading(false);
  };

  const cargarFecha = async (fechaId) => {
    const { data } = await supabase
      .from('ranking_fecha')
      .select('*')
      .eq('torneo_id', torneoId)
      .eq('fecha_id', fechaId)
      .order('posicion', { ascending: true });
    setRankingFecha(data || []);
  };

  const miUserId = session?.user?.id;

  const FilaRanking = ({ fila, index, mostrarFecha = false }) => {
    const esMio = fila.user_id === miUserId;
    const medal = index < 3 ? MEDALLAS[index] : null;
    const color = fila.avatar_color || tipoColor;

    return (
      <div style={{
        display: 'flex', alignItems: 'center', gap: '10px',
        padding: '10px 12px',
        background: esMio ? `${tipoColor}12` : '#0a0f19',
        border: `1px solid ${esMio ? tipoColor : '#1e293b'}`,
        borderRadius: '8px',
        transition: '0.2s',
      }}>
        {/* Posición */}
        <div style={{ minWidth: '28px', textAlign: 'center' }}>
          {medal
            ? <span style={{ fontSize: '1.1rem' }}>{medal}</span>
            : <span style={{ color: '#475569', fontSize: '0.85rem', fontWeight: 'bold' }}>#{fila.posicion}</span>
          }
        </div>

        {/* Avatar color */}
        <div style={{
          width: '30px', height: '30px', borderRadius: '50%',
          background: color, flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '0.75rem', fontWeight: 'bold', color: '#000',
        }}>
          {(fila.nickname || '?').slice(0, 2).toUpperCase()}
        </div>

        {/* Nombre */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{
            margin: 0, fontWeight: esMio ? 'bold' : '500',
            color: esMio ? tipoColor : '#e2e8f0',
            fontSize: '0.9rem',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {fila.perfiles?.clan_tag && (
              <span style={{ color: tipoColor, fontSize: '0.65rem', border: `1px solid ${tipoColor}55`, borderRadius: '4px', padding: '1px 5px', marginRight: '5px', letterSpacing: '1px' }}>[{fila.perfiles.clan_tag}]</span>
            )}
            {fila.nickname || 'Jugador'} {esMio && <span style={{ fontSize: '0.7rem', color: tipoColor }}>· vos</span>}
          </p>
          {mostrarFecha === false && (
            <p style={{ margin: 0, color: '#475569', fontSize: '0.7rem' }}>
              {fila.fechas_jugadas} fecha{fila.fechas_jugadas !== 1 ? 's' : ''} · mejor: {fila.mejor_fecha} pts
            </p>
          )}
        </div>

        {/* Puntos */}
        <div style={{ textAlign: 'right' }}>
          <p style={{
            margin: 0, fontWeight: 'bold', fontSize: '1.1rem',
            color: esMio ? tipoColor : '#fff',
            textShadow: esMio ? `0 0 10px ${tipoColor}88` : 'none',
          }}>
            {mostrarFecha ? fila.puntos : fila.puntos_total}
          </p>
          <p style={{ margin: 0, color: '#475569', fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>pts</p>
        </div>
      </div>
    );
  };

  if (loading) return (
    <div style={{ padding: '20px', textAlign: 'center', color: '#475569', fontSize: '0.85rem' }}>
      Cargando ranking...
    </div>
  );

  const fechaActual = fechas.find(f => f.id === vista);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>

      {/* Selector de vista */}
      <div style={{ display: 'flex', gap: '6px', overflowX: 'auto', paddingBottom: '2px' }}>
        {/* Botón General */}
        <button
          onClick={() => setVista('general')}
          style={{
            flexShrink: 0,
            padding: '6px 14px',
            background: vista === 'general' ? `${tipoColor}22` : '#0a0f19',
            border: `1px solid ${vista === 'general' ? tipoColor : '#1e293b'}`,
            borderRadius: '6px', cursor: 'pointer',
            color: vista === 'general' ? tipoColor : '#64748b',
            fontSize: '0.78rem', fontWeight: 'bold',
            transition: '0.2s',
          }}
        >
          General
        </button>

        {/* Botones por fecha */}
        {fechas.map(f => (
          <button
            key={f.id}
            onClick={() => setVista(f.id)}
            style={{
              flexShrink: 0,
              padding: '6px 12px',
              background: vista === f.id ? `${tipoColor}22` : '#0a0f19',
              border: `1px solid ${vista === f.id ? tipoColor : '#1e293b'}`,
              borderRadius: '6px', cursor: 'pointer',
              color: vista === f.id ? tipoColor : '#64748b',
              fontSize: '0.78rem', fontWeight: 'bold',
              transition: '0.2s',
              position: 'relative',
            }}
          >
            {f.nombre || `F${f.numero_fecha}`}
            {f.activa && !f.cerrada && (
              <span style={{
                position: 'absolute', top: '-4px', right: '-4px',
                width: '8px', height: '8px', borderRadius: '50%',
                background: '#22c55e',
                boxShadow: '0 0 6px #22c55e',
              }} />
            )}
          </button>
        ))}
      </div>

      {/* Tabla ranking */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        {vista === 'general' ? (
          ranking.length === 0
            ? <p style={{ color: '#334155', textAlign: 'center', fontSize: '0.85rem', margin: '12px 0' }}>Sin datos todavía</p>
            : ranking.map((fila, i) => <FilaRanking key={fila.user_id} fila={fila} index={i} mostrarFecha={false} />)
        ) : (
          rankingFecha.length === 0
            ? <p style={{ color: '#334155', textAlign: 'center', fontSize: '0.85rem', margin: '12px 0' }}>Sin puntos registrados en esta fecha</p>
            : rankingFecha.map((fila, i) => <FilaRanking key={fila.user_id} fila={fila} index={i} mostrarFecha={true} />)
        )}
      </div>

      {/* Footer con info del usuario */}
      {vista === 'general' && (() => {
        const miPosicion = ranking.find(r => r.user_id === miUserId);
        if (!miPosicion) return null;
        return (
          <div style={{
            borderTop: `1px solid #1e293b`, paddingTop: '8px', marginTop: '4px',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          }}>
            <span style={{ color: '#475569', fontSize: '0.72rem' }}>
              Tu posición: <strong style={{ color: tipoColor }}>#{miPosicion.posicion}</strong>
            </span>
            <span style={{ color: '#475569', fontSize: '0.72rem' }}>
              Total: <strong style={{ color: tipoColor }}>{miPosicion.puntos_total} pts</strong>
            </span>
          </div>
        );
      })()}
    </div>
  );
};

export default RankingTorneo;